// src/components/IssueLandingComponent/IssueLandingComponent.tsx
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Card, Spinner, Alert, Button } from 'react-bootstrap';
import { client } from '../../lib/AmplifyClient';
import type { Schema } from '../../../amplify/data/resource';

type Issue = Schema['Issue']['type'];

// ---- helpers ----
function unwrap<T>(maybeWrapped: unknown): T | null {
  if (maybeWrapped && typeof maybeWrapped === 'object' && 'data' in (maybeWrapped as any)) {
    const { data } = maybeWrapped as { data: T | null };
    return data ?? null;
  }
  return (maybeWrapped as T) ?? null;
}

function coerceStrings(arr?: (string | null)[] | null): string[] {
  return (arr ?? []).filter((s): s is string => typeof s === 'string' && s.trim().length > 0);
}

function parseAwsJson<T = any>(val: unknown): T | null {
  if (val == null) return null;
  if (typeof val === 'string') {
    try { return JSON.parse(val) as T; } catch { return null; }
  }
  if (typeof val === 'object') return val as T;
  return null;
}

function eq(a: string, b: string) {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

// Unified candidate type
type Candidate = { statement: string; score?: number };

function readCandidates(issue: Issue | null): Candidate[] {
  if (!issue) return [];

  const pjRaw = (issue as any).proposedIssueCandidatesJson;
  const pj = parseAwsJson<{ candidates?: Array<{ statement?: unknown; score?: unknown }> }>(pjRaw);

  const structured: Candidate[] = Array.isArray(pj?.candidates)
    ? pj!.candidates!
        .map((c): Candidate => ({
          statement: String((c as any)?.statement ?? '').trim(),
          score: typeof (c as any)?.score === 'number' ? ((c as any).score as number) : undefined,
        }))
        .filter((c) => c.statement.length > 0)
    : [];

  const mirrored: Candidate[] = coerceStrings((issue as any).reframedIssueCandidates).map(
    (s): Candidate => ({ statement: s, score: undefined })
  );

  const merged: Candidate[] = structured.length > 0 ? structured : mirrored;

  merged.sort(
    (a, b) =>
      (typeof b.score === 'number' ? b.score : -Infinity) -
      (typeof a.score === 'number' ? a.score : -Infinity)
  );
  return merged.slice(0, 4);
}

/** ---------- NEW: detect if 5 solutions already exist ---------- **/
function hasExistingSolutions(issue: any): boolean {
  if (!issue) return false;

  // 1) Newest AWSJSON shape: { solutions: [...] }
  const fromJson = parseAwsJson<any>(issue?.proposedSolutionsJson);
  if (fromJson && Array.isArray(fromJson.solutions) && fromJson.solutions.length > 0) return true;

  // 2) Legacy direct array (worker’s direct output)
  if (Array.isArray(issue?.solutions) && issue.solutions.length > 0) return true;

  // 3) Normalized array stored on the record (sessionSolutions)
  if (Array.isArray(issue?.sessionSolutions) && issue.sessionSolutions.length > 0) return true;

  return false;
}

// ---- component ----
export default function IssueLandingComponent() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const sessionId = useMemo(() => decodeURIComponent(id ?? '').trim().replace(/\/+$/, ''), [id]);

  const [loading, setLoading] = useState(true);
  const [issue, setIssue] = useState<Issue | null>(null);
  const [error, setError] = useState<string | null>(null);
  const mounted = useRef(true);

  async function readIssue() {
    const raw = (await client.models.Issue.get({ id: sessionId }, { authMode: 'userPool' })) as unknown;
    const data = unwrap<Issue>(raw);
    if (mounted.current) setIssue(data);
    return data;
  }

  useEffect(() => {
    mounted.current = true;
    if (!sessionId) return;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        await readIssue();
      } catch (e: any) {
        setError(e?.message ?? 'Failed to load session.');
      } finally {
        if (mounted.current) setLoading(false);
      }
    })();
    return () => {
      mounted.current = false;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" role="status" />
        <div className="mt-3">Loading…</div>
      </Container>
    );
  }

  if (error || !issue) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{error ?? 'Session not found.'}</Alert>
        <Button variant="outline-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
          Back to Session
        </Button>
      </Container>
    );
  }

  // Recompute agreed overlap from current DB state
  const candidates = readCandidates(issue);
  const initiatorYes = coerceStrings((issue as any)?.initiatorAcceptedIssues);
  const collaboratorYes = coerceStrings((issue as any)?.collaboratorAcceptedIssues);
  const agreedList: Candidate[] = candidates.filter(
    (c) => initiatorYes.some((s) => eq(s, c.statement)) && collaboratorYes.some((s) => eq(s, c.statement))
  );

  // Pick top scored agreed item (or first)
  const agreed: Candidate | null = agreedList[0] ?? null;

  const initiatorName =
    ((issue as any)?.initiatorFirstName || (issue as any)?.initiatorEmail || 'Initiator').toString();
  const collaboratorName =
    ((issue as any)?.collaboratorFirstName || (issue as any)?.collaboratorEmail || 'Collaborator').toString();

  // NEW: determine CTA label based on whether proposals already exist
  const proposalsExist = hasExistingSolutions(issue);
  const ctaLabel = proposalsExist ? 'View Solutions' : 'Create 5 Solutions';

  return (
    <Container className="mt-4">
      <Card className="mx-auto" style={{ maxWidth: 900 }}>
        <Card.Header as="h5">Agreed Issue</Card.Header>
        <Card.Body>
          {agreed ? (
            <>
              <Alert variant="success" className="mb-4">
                <div className="fw-semibold">
                  {collaboratorName} and {initiatorName} agreed on this issue:
                </div>
                <div className="mt-2 fs-5">“{agreed.statement}”</div>
              </Alert>

              <div className="d-flex gap-2">
                {/* Always go to solutions page. That page will either show existing proposals
                    or (if none) trigger generation; so we avoid double-enqueueing here. */}
                <Button
                  variant="primary"
                  onClick={() => navigate(`/session/${sessionId}/solutions`)}
                >
                  {ctaLabel}
                </Button>
                <Button variant="outline-secondary" onClick={() => navigate(`/session/${sessionId}`)}>
                  Back to Session
                </Button>
              </div>
            </>
          ) : (
            <>
              <Alert variant="warning" className="mb-3">
                It looks like there isn’t a confirmed overlap right now.
              </Alert>
              <Button variant="primary" onClick={() => navigate(`/session/${sessionId}/agree-issue`)}>
                Review Issues Again
              </Button>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
}
